<template>
  <v-layout class="info-containers-styled-overview" style="height: 160px; padding: 15px">
    <v-flex :class="lang === 'en' ? 'border-left' : 'border-right'">
      <v-layout column fill-height class="px-4">
        <v-layout>
          <img src="@/assets/images/tondo_icons/project_color.svg" :class="['icon-style', lang === 'en' ? 'mr-2' : 'ml-2']"/>
          <h2 class="font-weight-bold">{{ $t('PROJECT') }}</h2>
        </v-layout>
        <v-layout row justify-space-between align-end>
          <span class="font-weight-bold subheading" :class="lang === 'en' ? 'mr-2' : 'ml-2'">{{ $t('Total') }}</span>
          <span class="font-weight-bold indigo--text subheading">{{ filterDevices.length }}</span>
        </v-layout>
      </v-layout>
    </v-flex>
    <v-flex
      v-for="[type, states] in categoryMap.entries()"
      :key="`${type}`"
      :class="lang === 'en' ? 'border-left' : 'border-right'" 
      class="px-3"
    >
      <v-layout column justify-space-between fill-height d-flex wrap>
        <v-layout row>
          <img :src="getItemIcon(type)" :class="['icon-style', lang === 'en' ? 'mr-2' : 'ml-2']"/>
          <h2 class="font-weight-bold" style="white-space: nowrap">{{ $t(`${type}`) }}</h2>
        </v-layout>
        <v-layout row align-end justify-space-between>
          <v-layout column fill-height>
            <v-layout v-for="[state, count] in states" :key="`${state}`" align-center>
              <span class="font-weight-bold" style="white-space: nowrap;">{{ $t(state) }}:</span>
              <span class="font-weight-bold indigo--text px-2">{{ count }}</span>
            </v-layout>
          </v-layout>
          <v-layout column fill-height justify-space-around align-end>
            <v-progress-linear
              v-for="[state] in states"
              :key="`${state}-progress-bar`"
              :value="categoryPercentages.get(type).get(state)"
              height="12"
              color="#7E57C2"
              style="margin: 0; border-radius: 9px;"
              :style="categoryMap.size <= 3 ? 'width: 200px' : 'width: 100px'"
            >
            </v-progress-linear>
          </v-layout>
        </v-layout>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import Utils from '@/modules/Utils';
import PropUtils from '@/modules/PropUtils';

export default {
  name: 'StyledOverviewInfo',
  props: {
    filterDevices: {
      type: Array,
      default: () => []
    },
    lang: {
      type: String,
      default: 'en'
    }
  },
  data() {
    return {
      without_maintenance: ['water meter', 'flood sensor', 'motion sensor', 'barrier'],
      iconCache: {
        'CABINET': require('@/assets/images/tondo_icons/cabinet_color.svg'),
        'FIXTURE': require('@/assets/images/tondo_icons/fixture_color.svg'),
        'VIDEO': require('@/assets/images/tondo_icons/camera_color.svg'),
        'VIRTUAL FIXTURE': require('@/assets/images/tondo_icons/virtual_controller_color.svg'),
        'MOTION SENSOR': require('@/assets/images/tondo_icons/motion_sensor_color.svg'),
        'FLOOD SENSOR': require('@/assets/images/tondo_icons/flood_sensor_color.svg'),
        'BARRIER': require('@/assets/images/tondo_icons/barrier_color.svg'),
        'RADAR': require('@/assets/images/tondo_icons/radar_color.svg'),
        'WATER METER': require('@/assets/images/tondo_icons/water_meter_color.svg'),
        'WEATHER STATION': require('@/assets/images/tondo_icons/weather_station.png')
      }
    };
  },
  computed: {
    categoryData() {
      const categoryMap = new Map();
      const categoryCounter = new Map();
      const categoryPercentages = new Map();
      
      this.filterDevices.forEach((device) => {
        this.processCategoryMap(device, categoryMap, categoryCounter);
      });

      categoryCounter.forEach((count, type) => {
        const stateMap = categoryMap.get(type);
        const percentageMap = new Map();
        stateMap.forEach((stateCount, state) => {
          percentageMap.set(state, (stateCount / count) * 100);
        });
        categoryPercentages.set(type, percentageMap);
      });

      let ordered = [];
      const types = [];

      if (categoryMap.has('CABINET')){
        ordered.push(['CABINET', categoryMap.get('CABINET')]);
        types.push('CABINET');
      }
      if (categoryMap.has('FIXTURE')) {
        if (categoryMap.get('FIXTURE').has('Electrical Fault')) {
          categoryMap.get('FIXTURE').delete('Electrical Fault');
          categoryPercentages.get('FIXTURE').delete('Electrical Fault');
        }
        ordered.push(['FIXTURE', categoryMap.get('FIXTURE')]);
        types.push('FIXTURE');
      }

      if (types.length){
        ordered = ordered.concat([...categoryMap.entries()].filter(([type, value]) => !types.includes(type)));
      } else {
        ordered = [...categoryMap.entries()];
      }
      
      return {
        map: new Map(ordered),
        counter: categoryCounter,
        percentages: categoryPercentages
      };
    },
    categoryMap() {
      return this.categoryData.map;
    },
    categoryCounter() {
      return this.categoryData.counter;
    },
    categoryPercentages() {
      return this.categoryData.percentages;
    }
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  },
  methods: {
    getDeviceCategory(device) {
      let cat = device['meta.category'].category;
      if (cat === 'HUB') cat = 'CABINET';
      if (cat === 'ASSET' || (cat === 'FIXTURE' && device.class_name === 'virtual_controller')) cat = 'VIRTUAL FIXTURE';
      return cat;
    },
    getDeviceStatus(device, cat) {
      if (Utils.hasVirtualCabinetClass(device['meta.category'])) {
        return 'Virtual';
      }
      
      if (cat.toLowerCase() === 'fixture') {
        return PropUtils.getFieldLowerToUpper(Utils.getDeviceStatus(device));
      }
      
      if (device.sys___active === 0 || device.sys___active === null) return 'Inactive';
      if (device.sys___active === -1 && !this.without_maintenance.includes(cat.toLowerCase())) return 'Maintenance';
      return 'Active';
    },
    getInitialStateMap(device, cat) {
      if (cat.toLowerCase() === 'fixture') {
        return new Map([['Active', 0], ['Electrical Fault', 0], ['In Installation', 0], ['Fault In Care', 0], ['Maintenance', 0]]);
      }
      if (Utils.hasCabinetClass(device['meta.category'].category)) {
        return new Map([['Active', 0], ['Inactive', 0], ['Maintenance', 0], ['Virtual', 0]]);
      }
      if (this.without_maintenance.includes(cat.toLowerCase())) {
        return new Map([['Active', 0], ['Inactive', 0]]);
      }
      return new Map([['Active', 0], ['Inactive', 0], ['Maintenance', 0]]);
    },
    processCategoryMap(device, categoryMap, categoryCounter) {
      const cat = this.getDeviceCategory(device);
      if (!cat) return;
      
      if (!categoryMap.has(cat)) {
        categoryMap.set(cat, this.getInitialStateMap(device, cat));
        categoryCounter.set(cat, 0);
      }
      
      categoryCounter.set(cat, categoryCounter.get(cat) + 1);
      const status = this.getDeviceStatus(device, cat);
      const stateMap = categoryMap.get(cat);
      
      if (stateMap.has(status)) {
        stateMap.set(status, stateMap.get(status) + 1);
      }
    },
    getItemIcon(item) {
      return this.iconCache[item];
    }
  }
};
</script>

<style scoped>
.border-right {
  border-right: 3px solid #cfd8dc;
  border-radius: 3px;
}

.border-left {
  border-left: 3px solid #cfd8dc;
  border-radius: 3px;
}

.info-containers-styled-overview {
  overflow: auto;
  background-color: #F6F8FB;
}

.icon-style {
  height: 35px;
  width: 35px;
}

.progress-bar-div {
  white-space: nowrap;
  width: 55%;
  max-width: 55%;
  min-width: 20px;
  display: flex;
  align-items: center;
  text-align: center;
  padding: 0;
  margin: 0;
}

@media (max-width: 995px) and (orientation: landscape) {
  .progress-bar-div {
    width: 35%;
  }

  .maintenance-span-aside-pbar-text {
    width: 100%;
  }
}

.progress-bar-div-maintenance {
  width: 35%;
}

  /* width */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 5px;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #292f7d;
    border-radius: 5px;
  }
</style>